import { Fragment } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  IconButton,
  Button,
  InputLabel,
} from '@mui/material';
import { DeleteOutline, ControlPoint, ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { UseFormReturn, useFieldArray } from 'react-hook-form';
import {
  LineDetailsSchemaType,
  LineDetailsDeparturesSchemaType,
  emptyDeparture,
} from 'components/forms/CorporateCharter/types';
import { colors } from 'remote/global';
import DatePickerField from './FormFields/DatePickerField';
import DaysOfWeekPickerField from './FormFields/DaysOfWeekPickerField';
import VehicleSelectField from './FormFields/VehicleSelectField';
import TimePickerField from './FormFields/TimePickerField';

const additionalDeparturesColumns = [
  {
    field: 'startTime',
    minWidth: 100,
    flex: 1,
  },
  {
    field: 'scheduling',
    minWidth: 200,
    flex: 1.5,
  },
  {
    field: 'scheduleStart',
    minWidth: 100,
    flex: 1,
  },
  {
    field: 'scheduleEnd',
    minWidth: 100,
    flex: 1,
  },
  {
    field: 'licenseplate',
    minWidth: 100,
    flex: 1,
  },
];
const FIRST_ADDITIONAL_DEPARTURE_INDEX = 1;

interface AdditionalDeparture extends LineDetailsDeparturesSchemaType {
  id: string;
}

interface Props {
  formInstance: UseFormReturn<LineDetailsSchemaType>;
  filteredVehicles: string[];
}

function AdditionalDeparturesTable({ formInstance, filteredVehicles }: Props) {
  const { control, setValue, watch } = formInstance;
  const tInstance = useTranslation();
  const { t } = tInstance;
  const { fields, remove, append } = useFieldArray({
    name: 'departures',
    control,
  });
  const departuresWatch = watch('departures');

  return (
    <>
      <Typography
        sx={{
          color: colors.uiBlue600,
          fontSize: '18px',
          m: '24px 0',
          fw: 600,
        }}
      >
        {t('CorporateCharterView.steps.lineDetails.additionalDepartures.title')}
      </Typography>
      <Paper sx={{ width: '100%', background: 'transparent', overflowX: 'auto !important' }}>
        <Paper elevation={3}>
          <Table
            sx={{
              background: 'white',
              minWidth: '900px',
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: colors.primaryBlue5,
                }}
              >
                {additionalDeparturesColumns.map(({ field }) => {
                  return (
                    <TableCell
                      key={field}
                      sx={{
                        fontWeight: 600,
                        borderBottom: 'none',
                        color: colors.gray2,
                      }}
                    >
                      <span>
                        {t(
                          `CorporateCharterView.steps.lineDetails.additionalDepartures.table.header.${field}`
                        )}
                      </span>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((departure: AdditionalDeparture, i: number) => {
                if (i < FIRST_ADDITIONAL_DEPARTURE_INDEX) return null;
                const isReturnTripVisible = departuresWatch[i].returnTripVisible;

                return (
                  <Fragment key={departure.id}>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          maxWidth: '200px',
                        }}
                      >
                        <TimePickerField
                          formInstance={formInstance}
                          translationKey=''
                          name={`departures.${i}.clientDepartureTime`}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          width: '200px',
                          maxWidth: '200px',
                        }}
                      >
                        <DaysOfWeekPickerField
                          formInstance={formInstance}
                          name={`departures.${i}.schedule.daysOfWeek`}
                          translationKey=''
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          maxWidth: '200px',
                        }}
                      >
                        <DatePickerField
                          formInstance={formInstance}
                          name={`departures.${i}.schedule.startsOn`}
                          translationKey=''
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          maxWidth: '200px',
                        }}
                      >
                        <DatePickerField
                          formInstance={formInstance}
                          name={`departures.${i}.schedule.endsOn`}
                          translationKey=''
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          width: '240px',
                        }}
                      >
                        <Box display='flex' width='100%'>
                          <Box width='140px'>
                            <VehicleSelectField
                              formInstance={formInstance}
                              name={`departures.${i}.licensePlate`}
                              translationKey=''
                              filteredVehicles={filteredVehicles}
                            />
                          </Box>
                          <IconButton
                            size='large'
                            aria-label='Remove departure'
                            aria-haspopup='true'
                            sx={{
                              padding: 0,
                              color: colors.gray2,
                              ml: '18px',
                              mr: '18px',
                            }}
                            onClick={() => remove(i)}
                          >
                            <DeleteOutline color='primary' />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Box display='flex' sx={{ alignItems: 'center' }}>
                          <InputLabel
                            sx={{
                              fontSize: '14px',
                              fontWeight: 500,
                              color: colors.gray2,
                              marginRight: '8px',
                            }}
                            aria-label={`departures.${i}.clientReturnTime`}
                            htmlFor={`departures.${i}.clientReturnTime`}
                            shrink
                          >
                            {t('CorporateCharterView.steps.schedule.returnTrip.title')}
                          </InputLabel>
                          {isReturnTripVisible ? (
                            <Box id='clientReturnTime' sx={{ display: 'flex', maxWidth: '238px' }}>
                              <TimePickerField
                                formInstance={formInstance}
                                name={`departures.${i}.clientReturnTime`}
                              />
                              <IconButton
                                size='large'
                                aria-label='Remove return trip'
                                sx={{
                                  padding: 0,
                                  color: colors.gray2,
                                  ml: '18px',
                                  mr: '18px',
                                }}
                                onClick={() => {
                                  const index = fields.findIndex((d) => d.id === departure.id);
                                  setValue(`departures.${index}.returnTripVisible`, false);
                                  setValue(`departures.${index}.clientReturnTime`, null);
                                }}
                              >
                                <DeleteOutline color='primary' />
                              </IconButton>
                            </Box>
                          ) : (
                            <Button
                              onClick={() => {
                                setValue(`departures.${i}.returnTripVisible`, true);
                              }}
                              startIcon={<ControlPoint />}
                              endIcon={<ExpandMore />}
                            >
                              {t(
                                'CorporateCharterView.steps.schedule.returnTrip.actions.addReturn'
                              )}
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button variant='text' onClick={() => append(emptyDeparture)}>
              {t(
                'CorporateCharterView.steps.lineDetails.additionalDepartures.actions.addDeparture'
              )}
            </Button>
          </Box>
        </Paper>
      </Paper>
    </>
  );
}

export default AdditionalDeparturesTable;
